import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Card, CardContent, Grid, Box, Button,
  Avatar, Chip, TextField, IconButton, Rating, Divider, Paper,
  Collapse, Alert, CircularProgress, Badge, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemText, ListItemIcon, ListItemSecondaryAction,
  LinearProgress
} from '@mui/material';
import {
  Person, Edit, PhotoLibrary, LocationOn, Work, Phone,
  Verified, CheckCircle, Add, Delete, ExpandMore, ExpandLess,
  Save, Cancel, Settings, Visibility, Construction, School,
  Image, Share
} from '@mui/icons-material';
import api from '../services/api';
import FileUpload from '../components/FileUpload';
import UnifiedOnboardingModal from '../components/onboarding/UnifiedOnboardingModal';
import ZimAddressFields from '../components/common/ZimAddressFields';
import DialogCloseButton from '../components/common/DialogCloseButton';
import { brand } from '../theme/colors';

const BRAND_PRIMARY = brand.primary;
const BRAND_SECONDARY = brand.primaryDark;
const BRAND_ACCENT = brand.secondary;

const parseImages = (images) => {
  if (!images) return [];
  if (Array.isArray(images)) return images;
  if (typeof images === 'string') {
    try {
      const parsed = JSON.parse(images);
      return Array.isArray(parsed) ? parsed : [images];
    } catch (_) {
      return images.trim() ? [images] : [];
    }
  }
  return [];
};

const isVerifiablePortfolioItem = (item) => {
  const afterImages = parseImages(item.after_images || item.afterImages);
  return Boolean(
    item.is_public !== false &&
    (item.project_title || item.projectTitle || item.title || '').trim() &&
    (item.description || '').trim().length >= 20 &&
    (item.project_type || item.projectType || item.project_category || item.projectCategory || '').trim() &&
    (item.city || item.location || '').trim() &&
    (item.completion_date || item.completionDate) &&
    afterImages.length > 0
  );
};

/**
 * Contractor Profile Page
 * Layout: Hero → Completeness bar → Content sections
 */
const ContractorProfilePage = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    about: true,
    skills: true,
    portfolio: true,
    certifications: true,
    settings: false
  });

  const [editMode, setEditMode] = useState({});

  const [changePhotoOpen, setChangePhotoOpen] = useState(false);
  const [addSkillOpen, setAddSkillOpen] = useState(false);
  const [addCertOpen, setAddCertOpen] = useState(false);
  const [newSkill, setNewSkill] = useState({ name: '', level: 'Intermediate' });
  const [newCert, setNewCert] = useState({ name: '', issuer: '', date: '' });

  const [profileData, setProfileData] = useState({
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      whatsapp: '',
      location: '',
      province: '',
      district: '',
      suburb: '',
      address: '',
      avatar: null,
      bio: ''
    },
    businessInfo: {
      businessName: '',
      yearsExperience: '',
      teamSize: '',
      specialties: []
    },
    skills: [],
    certifications: [],
    portfolio: [],
    availability: {
      available: true,
      serviceAreas: []
    },
    settings: {
      profileVisibility: true,
      showContactInfo: true,
      emailNotifications: true
    },
    stats: {
      rating: 0,
      reviewCount: 0,
      completedProjects: 0,
      onTimeRate: 0
    }
  });

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contractors/profile/me');
      const data = response.data;

      if (data.success) {
        setProfileData({
          personalInfo: {
            firstName: data.data.firstName || '',
            lastName: data.data.lastName || '',
            email: data.data.email || '',
            phone: data.data.phone || '',
            whatsapp: data.data.whatsapp || '',
            location: data.data.location || '',
            province: data.data.province || '',
            district: data.data.district || '',
            suburb: data.data.suburb || '',
            address: data.data.address || '',
            avatar: data.data.profileImageUrl || null,
            bio: data.data.bio || ''
          },
          businessInfo: {
            businessName: data.data.businessName || '',
            yearsExperience: data.data.yearsExperience || '',
            teamSize: data.data.teamSize || '1-5',
            specialties: data.data.specialties || []
          },
          skills: data.data.skills || [],
          certifications: data.data.certifications || [],
          portfolio: data.data.portfolio || [],
          availability: {
            available: data.data.available !== false,
            serviceAreas: data.data.serviceAreas || []
          },
          settings: {
            profileVisibility: data.data.profileVisibility !== false,
            showContactInfo: data.data.showContactInfo !== false,
            emailNotifications: data.data.emailNotifications !== false
          },
          stats: {
            rating: data.data.rating || 0,
            reviewCount: data.data.reviewCount || 0,
            completedProjects: data.data.completedProjects || 0,
            onTimeRate: data.data.onTimeRate || 0
          }
        });
      } else {
        throw new Error(data.message || `Failed to load profile (${response.status})`);
      }
    } catch (err) {
      setError(err.message || 'Failed to load profile');
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const response = await api.put('/contractors/profile/me', {
        firstName: profileData.personalInfo.firstName,
        lastName: profileData.personalInfo.lastName,
        bio: profileData.personalInfo.bio,
        phoneNumber: profileData.personalInfo.phone,
        city: profileData.personalInfo.location,
        province: profileData.personalInfo.province,
        district: profileData.personalInfo.district,
        suburb: profileData.personalInfo.suburb,
        address: profileData.personalInfo.address,
        companyName: profileData.businessInfo.businessName,
        yearsExperience: profileData.businessInfo.yearsExperience,
        specializations: profileData.businessInfo.specialties,
        serviceAreas: profileData.availability.serviceAreas,
        isAvailable: profileData.availability.available
      });

      const data = response.data;
      if (data.success) {
        updateUser({
      profile: {
            ...user?.profile,
            firstName: profileData.personalInfo.firstName,
            lastName: profileData.personalInfo.lastName,
            phoneNumber: profileData.personalInfo.phone,
            city: profileData.personalInfo.location,
            bio: profileData.personalInfo.bio,
            yearsExperience: profileData.businessInfo.yearsExperience,
            companyName: profileData.businessInfo.businessName
          }
        });
        setSuccess('Profile updated successfully!');
        setEditMode({});
      } else {
        throw new Error(data.message || 'Failed to save profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
      setTimeout(() => { setSuccess(''); setError(''); }, 5000);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleEditMode = (section) => {
    setEditMode(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handlePhotoUploadComplete = async (uploadData) => {
    try {
      const response = await api.put('/contractors/profile/me', { profileImageUrl: uploadData.url });
      const data = response.data;
      if (data.success) {
        setProfileData(prev => ({
          ...prev,
          personalInfo: { ...prev.personalInfo, avatar: uploadData.url }
        }));
        setSuccess('Profile picture updated!');
        setChangePhotoOpen(false);
      }
    } catch (err) {
      setError('Failed to save profile picture');
    }
  };

  const handleAddSkill = async () => {
    const specialization = newSkill.name.trim();
    if (!specialization) return;

    const nextSpecialties = Array.from(new Set([
      ...profileData.businessInfo.specialties,
      specialization
    ]));

    try {
      await api.put('/contractors/profile/me', { specializations: nextSpecialties });
      setProfileData(prev => ({
        ...prev,
        businessInfo: { ...prev.businessInfo, specialties: nextSpecialties },
        skills: nextSpecialties.map((name, index) => ({ id: `spec-${index}`, name, level: 'Expert' }))
      }));
      setNewSkill({ name: '', level: 'Intermediate' });
      setAddSkillOpen(false);
      setSuccess('Specialization saved.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save specialization');
    } finally {
      setTimeout(() => { setSuccess(''); setError(''); }, 3000);
    }
  };

  const handleRemoveSkill = async (skillId) => {
    const removed = profileData.skills.find(skill => skill.id === skillId);
    const nextSpecialties = profileData.businessInfo.specialties.filter(spec => spec !== removed?.name);

    try {
      await api.put('/contractors/profile/me', { specializations: nextSpecialties });
      setProfileData(prev => ({
        ...prev,
        businessInfo: { ...prev.businessInfo, specialties: nextSpecialties },
        skills: nextSpecialties.map((name, index) => ({ id: `spec-${index}`, name, level: 'Expert' }))
      }));
      setSuccess('Specialization removed.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove specialization');
    } finally {
      setTimeout(() => { setSuccess(''); setError(''); }, 3000);
    }
  };

  const handleAddCertification = async () => {
    if (!newCert.name.trim()) return;

    try {
      const response = await api.post('/contractors/certifications', {
        certification_name: newCert.name,
        issuing_organization: newCert.issuer || undefined,
        issue_date: newCert.date || undefined
      });
      const cert = response.data.data;
      setProfileData(prev => ({
        ...prev,
        certifications: [...prev.certifications, {
          id: cert.id,
          name: cert.certification_name,
          issuer: cert.issuing_organization || '',
          date: cert.issue_date || ''
        }]
      }));
      setNewCert({ name: '', issuer: '', date: '' });
      setAddCertOpen(false);
      setSuccess('Certification saved.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save certification');
    } finally {
      setTimeout(() => { setSuccess(''); setError(''); }, 3000);
    }
  };

  const handleRemoveCertification = async (certId) => {
    try {
      await api.delete(`/contractors/certifications/${certId}`);
      setProfileData(prev => ({
        ...prev,
        certifications: prev.certifications.filter(c => c.id !== certId)
      }));
      setSuccess('Certification removed.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove certification');
    } finally {
      setTimeout(() => { setSuccess(''); setError(''); }, 3000);
    }
  };

  const getSkillLevelColor = (level) => {
    if (!level) return 'default';
    const colors = { Expert: 'success', Advanced: 'primary', Intermediate: 'warning', Beginner: 'info' };
    return colors[level] || 'default';
  };

  const calculateProfileCompleteness = () => {
    const specCount = (profileData.businessInfo.specialties || []).length;
    const hasVerifiablePortfolio = profileData.portfolio.some(isVerifiablePortfolioItem);
    const checks = [
      profileData.personalInfo.bio && profileData.personalInfo.bio.length > 50,
      specCount >= 3,
      profileData.businessInfo.yearsExperience,
      profileData.personalInfo.province,
      hasVerifiablePortfolio
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  };

  const getMissingItems = () => {
    const items = [];
    const specCount = (profileData.businessInfo.specialties || []).length;
    const hasVerifiablePortfolio = profileData.portfolio.some(isVerifiablePortfolioItem);
    if (!profileData.personalInfo.bio || profileData.personalInfo.bio.length <= 50) items.push('Bio (50+ chars)');
    if (specCount < 3) items.push(`${3 - specCount} more specialization${3 - specCount !== 1 ? 's' : ''}`);
    if (!profileData.businessInfo.yearsExperience) items.push('Years experience');
    if (!profileData.personalInfo.province) items.push('Location');
    if (!hasVerifiablePortfolio) items.push('Verifiable portfolio project');
    return items.slice(0, 4);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} sx={{ color: BRAND_PRIMARY }} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading your profile...</Typography>
      </Container>
    );
  }

  const completeness = calculateProfileCompleteness();
  const missingItems = getMissingItems();
  const { personalInfo, businessInfo, stats } = profileData;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {showOnboarding && <UnifiedOnboardingModal userRole="contractor" onComplete={() => { setShowOnboarding(false); fetchProfileData(); setSuccess('Profile updated successfully!'); setTimeout(() => setSuccess(''), 5000); }} />}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ===== 1. HERO ===== */}
      <Card sx={{ mb: 3, overflow: 'visible', boxShadow: 3 }}>
        {/* Gradient banner */}
        <Box sx={{
          height: 180,
          background: brand.gradient,
          borderRadius: '4px 4px 0 0'
        }} />

        <CardContent sx={{ textAlign: 'center' }}>
          {/* Centered avatar */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: '-60px', mb: 2 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <IconButton
                  size="small"
                  onClick={() => setChangePhotoOpen(true)}
                  sx={{ bgcolor: BRAND_PRIMARY, color: 'white', '&:hover': { bgcolor: BRAND_ACCENT }, width: 32, height: 32 }}
                >
                  <PhotoLibrary sx={{ fontSize: 14 }} />
                </IconButton>
              }
            >
              <Avatar
                src={personalInfo.avatar || ''}
                sx={{ width: 120, height: 120, border: '4px solid white', boxShadow: 3 }}
              >
                {personalInfo.firstName?.charAt(0) || 'C'}
              </Avatar>
            </Badge>
          </Box>

          {/* Name + business */}
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            {personalInfo.firstName || 'Your'} {personalInfo.lastName || 'Name'}
            {stats.rating >= 4.5 && (
              <Tooltip title="Verified Professional">
                <Verified sx={{ color: BRAND_PRIMARY, fontSize: 20, ml: 1, verticalAlign: 'middle' }} />
              </Tooltip>
            )}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
            {businessInfo.businessName || 'Contractor'}
          </Typography>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Visibility />}
              onClick={() => navigate(`/contractors/${user?.id}`)}
              sx={{ borderColor: BRAND_PRIMARY, color: BRAND_PRIMARY }}
            >
              View Public Profile
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Share />}
              onClick={() => {
                const url = `${window.location.origin}/contractors/${user?.id}`;
                navigator.clipboard.writeText(url);
              }}
              sx={{ borderColor: BRAND_PRIMARY, color: BRAND_PRIMARY }}
            >
              Share Profile
            </Button>
          </Box>

          {/* Info chips */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {(() => {
              const composedLocation = [personalInfo.suburb, personalInfo.district, personalInfo.province].filter(Boolean).join(', ') || personalInfo.location;
              return composedLocation ? (
                <Chip icon={<LocationOn />} label={composedLocation} size="small" variant="outlined" />
              ) : null;
            })()}
            {businessInfo.yearsExperience && (
              <Chip icon={<Work />} label={`${businessInfo.yearsExperience} yrs experience`} size="small" variant="outlined" />
            )}
            {personalInfo.phone && (
              <Chip icon={<Phone />} label={personalInfo.phone} size="small" variant="outlined" />
            )}
          </Box>

          {/* Stats */}
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#ffd700' }}>
                  {stats.rating.toFixed(1)}
                </Typography>
                <Rating value={stats.rating} readOnly size="small" max={5} />
              </Box>
              <Typography variant="caption" color="text.secondary">Rating</Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: BRAND_PRIMARY }}>
                {stats.completedProjects}
              </Typography>
              <Typography variant="caption" color="text.secondary">Projects</Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: BRAND_PRIMARY }}>
                {stats.onTimeRate ? `${stats.onTimeRate}%` : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.onTimeRate ? 'On-Time' : 'Not enough data'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {stats.reviewCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">Reviews</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ===== 1b. THE REPUTATION RULE — said once, plainly, where the contractor sees their own numbers ===== */}
      <Card sx={{ mb: 3, borderLeft: `5px solid ${BRAND_PRIMARY}` }}>
        <CardContent sx={{ py: '14px !important' }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5 }}>
            How your Eqaya name is built
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Your rating, your reviews, your job count and your verified badge are shown to every client who looks at you.
            <strong> Only work paid through the Eqaya project wallet counts. A cash job builds nothing here.</strong>
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { fontSize: '0.875rem', color: 'text.secondary', mb: 0.25 } }}>
            <li>A client can only review you after a stage has been paid to you through Eqaya.</li>
            <li>"Jobs paid through Eqaya" counts every wallet that has released money to you.</li>
            <li>Talk by phone all you like. When you agree a price, send the quotation on Eqaya so the job counts.</li>
          </Box>
        </CardContent>
      </Card>

      {/* ===== 2. COMPLETENESS BAR (hidden at 100%) ===== */}
      {completeness < 100 && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: '12px !important' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body1" fontWeight={600}>
                Profile {completeness}% complete
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setShowOnboarding(true)}
                  sx={{ bgcolor: BRAND_PRIMARY, '&:hover': { bgcolor: BRAND_SECONDARY }, textTransform: 'none', fontWeight: 600, py: 0.5 }}
                >
                  Complete Profile →
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {completeness >= 80 ? 'Looking great!' : completeness >= 50 ? 'Almost there' : 'Needs attention'}
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={completeness}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: '#eef0fd',
                '& .MuiLinearProgress-bar': {
                  bgcolor: completeness >= 80 ? BRAND_PRIMARY : '#FAA61A',
                  borderRadius: 4
                }
              }}
            />
            {missingItems.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Missing:</Typography>
                {missingItems.map(item => (
                  <Chip
                    key={item}
                    label={item}
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#fff8e1', color: '#e65100', border: '1px solid #ffe082' }}
                  />
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== 3. MAIN BODY ===== */}

          {/* ABOUT ME */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: editMode.about || personalInfo.bio ? 2 : 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person sx={{ color: BRAND_PRIMARY }} />
                  <Typography variant="h6">About Me</Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => toggleEditMode('about')}
                  sx={{ color: editMode.about ? 'error.main' : BRAND_PRIMARY }}
                >
                  {editMode.about ? <Cancel /> : <Edit />}
                </IconButton>
              </Box>

              {editMode.about ? (
                <Box>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First name"
                        value={personalInfo.firstName}
                        onChange={(e) => setProfileData({
                          ...profileData,
                          personalInfo: { ...personalInfo, firstName: e.target.value }
                        })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last name"
                        value={personalInfo.lastName}
                        onChange={(e) => setProfileData({
                          ...profileData,
                          personalInfo: { ...personalInfo, lastName: e.target.value }
                        })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone number"
                        value={personalInfo.phone}
                        onChange={(e) => setProfileData({
                          ...profileData,
                          personalInfo: { ...personalInfo, phone: e.target.value, whatsapp: e.target.value }
                        })}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ZimAddressFields
                        value={{
                          province: personalInfo.province,
                          district: personalInfo.district,
                          suburb: personalInfo.suburb,
                          address: personalInfo.address,
                        }}
                        onChange={(next) => setProfileData({
                          ...profileData,
                          personalInfo: { ...personalInfo, ...next },
                        })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Years experience"
                        type="number"
                        value={businessInfo.yearsExperience}
                        onChange={(e) => setProfileData({
                          ...profileData,
                          businessInfo: { ...businessInfo, yearsExperience: e.target.value }
                        })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Business name"
                        value={businessInfo.businessName}
                        onChange={(e) => setProfileData({
                          ...profileData,
                          businessInfo: { ...businessInfo, businessName: e.target.value }
                        })}
                      />
                    </Grid>
                  </Grid>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Professional Bio"
                    value={personalInfo.bio}
                    onChange={(e) => setProfileData({
                      ...profileData,
                      personalInfo: { ...personalInfo, bio: e.target.value }
                    })}
                    sx={{ mb: 2 }}
                    helperText={`${personalInfo.bio.length} characters — aim for 50+ to boost profile score`}
                  />
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSaveProfile}
                    disabled={saving}
                    sx={{ bgcolor: BRAND_PRIMARY, '&:hover': { bgcolor: BRAND_ACCENT } }}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              ) : (
                <Typography
                  variant="body1"
                  color={personalInfo.bio ? 'text.primary' : 'text.secondary'}
                  sx={{ lineHeight: 1.75, fontStyle: personalInfo.bio ? 'normal' : 'italic' }}
                >
                  {personalInfo.bio || 'No bio added yet. Click the edit icon to write your professional bio.'}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* SPECIALIZATIONS */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Construction sx={{ color: BRAND_PRIMARY }} />
                  <Typography variant="h6">Specializations</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Add skill">
                    <IconButton size="small" onClick={() => setAddSkillOpen(true)} sx={{ color: BRAND_PRIMARY }}>
                      <Add />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => toggleSection('skills')}>
                    {expandedSections.skills ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
              </Box>

              <Collapse in={expandedSections.skills}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {profileData.skills.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No specializations added yet. Add at least three so clients can match you to the right work.
                    </Typography>
                  ) : (
                    profileData.skills.map((skill) => (
                      <Chip
                        key={skill.id}
                        label={skill.name}
                        color={getSkillLevelColor(skill.level)}
                        onDelete={() => handleRemoveSkill(skill.id)}
                        size="small"
                      />
                    ))
                  )}
                </Box>
              </Collapse>
            </CardContent>
          </Card>

          {/* CERTIFICATIONS */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <School sx={{ color: BRAND_PRIMARY }} />
                  <Typography variant="h6">Certifications</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Add certification">
                    <IconButton size="small" onClick={() => setAddCertOpen(true)} sx={{ color: BRAND_PRIMARY }}>
                      <Add />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => toggleSection('certifications')}>
                    {expandedSections.certifications ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
              </Box>

              <Collapse in={expandedSections.certifications}>
                {profileData.certifications.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No certifications added yet. Click + to add certifications.
                  </Typography>
                ) : (
                  <List disablePadding>
                    {profileData.certifications.map((cert) => (
                      <ListItem key={cert.id} disablePadding sx={{ py: 0.75 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircle sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={600}>{cert.name}</Typography>
                          }
                          secondary={
                            [cert.issuer, cert.date].filter(Boolean).join(' · ')
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" size="small" onClick={() => handleRemoveCertification(cert.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Collapse>
            </CardContent>
          </Card>

          {/* PORTFOLIO PREVIEW */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Image sx={{ color: BRAND_PRIMARY }} />
                  <Typography variant="h6">Portfolio</Typography>
                  {profileData.portfolio.length > 0 && (
                    <Chip
                      label={profileData.portfolio.length}
                      size="small"
                      sx={{ height: 20, bgcolor: '#f0f1fe', color: BRAND_SECONDARY, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {profileData.portfolio.length > 3 && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => navigate('/contractor/portfolio')}
                      sx={{ color: BRAND_PRIMARY, borderColor: BRAND_PRIMARY, fontSize: '0.75rem', py: 0.25 }}
                    >
                      View All
                    </Button>
                  )}
                  <IconButton size="small" onClick={() => toggleSection('portfolio')}>
                    {expandedSections.portfolio ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
              </Box>

              <Collapse in={expandedSections.portfolio}>
                {profileData.portfolio.length === 0 ? (
                  <Box sx={{
                    textAlign: 'center', py: 4, bgcolor: '#fafafa',
                    borderRadius: 1, border: '2px dashed #e0e0e0'
                  }}>
                    <Image sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No portfolio items yet. Showcase your completed projects.
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => navigate('/contractor/portfolio')}
                      sx={{ mt: 2, bgcolor: BRAND_PRIMARY, '&:hover': { bgcolor: BRAND_SECONDARY } }}
                    >
                      Add Your First Project
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={2}>
                      {profileData.portfolio.slice(0, 3).map((item, index) => (
                        <Grid item xs={12} sm={4} key={index}>
                          <Paper sx={{ overflow: 'hidden', borderRadius: 1 }} elevation={1}>
                            <img
                              src={item.url}
                              alt={item.title}
                              style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                            />
                            <Box sx={{ p: 1 }}>
                              <Typography variant="caption" fontWeight={600} noWrap display="block">
                                {item.title}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                    {profileData.portfolio.length > 3 && (
                      <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Button
                          variant="text"
                          onClick={() => navigate('/contractor/portfolio')}
                          sx={{ color: BRAND_PRIMARY }}
                        >
                          View all {profileData.portfolio.length} portfolio items →
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </Collapse>
            </CardContent>
          </Card>
          {/* SETTINGS */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => toggleSection('settings')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Settings sx={{ color: BRAND_PRIMARY }} />
                  <Typography variant="subtitle1" fontWeight={600}>Settings & Preferences</Typography>
                </Box>
                <IconButton size="small">
                  {expandedSections.settings ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              <Collapse in={expandedSections.settings}>
                <List disablePadding dense sx={{ mt: 1 }}>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemText
                      primary="Profile Visibility"
                      secondary="Visible to clients"
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        label={profileData.settings.profileVisibility ? 'Public' : 'Private'}
                        color={profileData.settings.profileVisibility ? 'success' : 'default'}
                        size="small"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemText primary="Show Contact Info" />
                    <ListItemSecondaryAction>
                      <Chip
                        label={profileData.settings.showContactInfo ? 'Yes' : 'No'}
                        color={profileData.settings.showContactInfo ? 'success' : 'default'}
                        size="small"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemText primary="Email Notifications" />
                    <ListItemSecondaryAction>
                      <Chip
                        label={profileData.settings.emailNotifications ? 'On' : 'Off'}
                        color={profileData.settings.emailNotifications ? 'success' : 'default'}
                        size="small"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </Collapse>
            </CardContent>
          </Card>

      {/* ===== DIALOGS ===== */}

      {/* Change Photo */}
      <Dialog open={changePhotoOpen} onClose={() => setChangePhotoOpen(false)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => setChangePhotoOpen(false)} />
        <DialogTitle>Change Profile Picture</DialogTitle>
        <DialogContent>
          <FileUpload
            onUploadComplete={handlePhotoUploadComplete}
            onUploadError={(err) => setError(err.message)}
            acceptedFileTypes="image/*"
            maxFileSize={5}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePhotoOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Add Specialization */}
      <Dialog open={addSkillOpen} onClose={() => setAddSkillOpen(false)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => setAddSkillOpen(false)} />
        <DialogTitle>Add Specialization</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Specialization"
            value={newSkill.name}
            onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSkillOpen(false)}>Cancel</Button>
          <Button onClick={handleAddSkill} variant="contained"
            sx={{ bgcolor: BRAND_PRIMARY, '&:hover': { bgcolor: BRAND_ACCENT } }}>
            Save Specialization
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Certification */}
      <Dialog open={addCertOpen} onClose={() => setAddCertOpen(false)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => setAddCertOpen(false)} />
        <DialogTitle>Add Certification</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Certification Name"
            value={newCert.name}
            onChange={(e) => setNewCert({ ...newCert, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Issuing Organization"
            value={newCert.issuer}
            onChange={(e) => setNewCert({ ...newCert, issuer: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="date"
            label="Date Obtained"
            value={newCert.date}
            onChange={(e) => setNewCert({ ...newCert, date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCertOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCertification} variant="contained"
            sx={{ bgcolor: BRAND_PRIMARY, '&:hover': { bgcolor: BRAND_ACCENT } }}>
            Add Certification
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContractorProfilePage;
